import React from 'react';
import { TChildren } from '@models/common';
import { isChunkLoadError, logError, reloadOnChunkError } from '@utils/errors';

type Props = {
  children: TChildren;
  fallback?: React.ReactNode;
};

type State = {
  hasError: boolean;
};

// Local boundary so a crashing island never takes down the host page. We do NOT
// reuse SentryErrorBoundary from the main app: importing @services/sentry/sentry
// calls Sentry.init() at module load and would hijack the host page's Sentry hub.
// logError() reports through the island's own client (see islandSentry.ts).
class IslandErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    // A tab opened before a deploy requests island chunks that no longer
    // exist — reload once to pick up the current island.js.
    if (isChunkLoadError(error)) {
      reloadOnChunkError(error);
      return;
    }

    logError(error, { source: 'island' });
  }

  render() {
    const { hasError } = this.state;
    const { children, fallback = null } = this.props;

    if (hasError) {
      return fallback;
    }

    return children;
  }
}

export default IslandErrorBoundary;
